import { useState, useEffect, useCallback, useRef } from 'react';
import { auth, db, onAuthStateChanged, getRedirectResult, logout } from '../firebase';
import { doc, onSnapshot, setDoc, updateDoc, getDoc, collection, query, where, getDocs, addDoc, increment, deleteDoc } from 'firebase/firestore';
import { GoogleAuthProvider, signInWithCredential, signInWithCustomToken, deleteUser, signOut, reauthenticateWithCredential, reauthenticateWithRedirect } from 'firebase/auth';
import { differenceInMinutes, addMinutes, startOfDay, startOfMonth } from 'date-fns';
import { calculateExp, getExpToNextLevel } from '../utils/gameLogic';
import { Reward } from '../utils/rewardSystem';
import { DAILY_REWARD_TRACK, DailyReward, SingleReward } from '../utils/dailyRewards';
import { Prism, generatePrism, evolvePrism, rerollPrism as rerollPrismLogic, calculatePrismBuffs, rollAnimaTicketStars, generateBlackStats } from '../utils/prismSystem';
import { MILESTONE_OFFERS } from '../utils/milestoneOffers';
import { validateNickname } from '../utils/validation';
import { Capacitor } from '@capacitor/core';
import { FirebaseAuthentication } from '@capacitor-firebase/authentication';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { AVATARS } from '../utils/avatars';
import { purchaseManager } from '../utils/purchaseManager';
export interface UserData {
  uid: string;
  // Category 1: Profile & Settings
  displayName: string | null;
  nickname?: string | null;
  photoURL: string | null;
  avatarId?: string;
  purchasedAvatars?: string[];
  activeMilestoneOffer?: { id: string; expiresAt: string; } | null;
  purchasedMilestoneOffers?: string[];
  expiredMilestoneOffers?: string[];
  isPremium?: boolean;
  isAnonymous?: boolean;

  // Category 2: Economy
  coins: number;
  gems: number;
  heartTickets: number;
  prismTickets: number;
  starTickets: number;
  animaTickets: {
    1: number;
    2: number;
    3: number;
  };
  user_credits: number;
  is_pro_member: boolean;
  subscription_expiry?: any;
  prismInventory: Prism[];
  equippedPrismId: string | null;
  equippedPrismIds?: {
    pemdas: string | null;
    shellcrush: string | null;
    goldrush: string | null;
  };
  selectedTheme?: string;
  unlockedThemes?: string[];
  badges?: string[];

  // Category 3: Progression
  level: number;
  exp: number;
  totalExp: number;
  hearts: number;
  lastHeartRestoreTime: string;
  equippedBadgeId?: string;
  highScores: {
    pemdas: number;
    shellcrush: number;
    starburst: number;
    goldrush: number;
  };
  
  // Category 4: Mission Tracking
  counters: {
    lifetimeCorrect: number;
    lifetimeGames: number;
    dailyGames: number;
    monthlyGames: number;
    coinsSpent: number;
    heartsSent: number;
    // Vanguard Tracking
    blocks_destroyed: number;
    hearts_consumed: number;
    fever_count: number;
    total_gc_spent: number;
    total_cc_spent: number;
    plays_starburst: number;
    plays_cloud: number;
    plays_gold: number;
    plays_shell: number;
    animaTicketsOpened?: number;
  };
  collections?: {
    vanguard_borders: string[];
    vanguard_sets: string[]; // Owned mission sets
  };

  // Category 5: Daily Gift System
  dailyGift: {
    lastClaimedDate: string | null;
    currentStreak: number;
    isClaimedToday: boolean;
    isDoubledToday?: boolean;
  };
  proGift: {
    lastClaimedDate: string | null;
    currentDay: number; // 0-27 (for 28 days)
    isClaimedToday: boolean;
  };

  // Utility/Meta
  lastLoginDate?: string;
  dailyResetUTC?: string;
  monthlyResetUTC?: string;
  lastTicketSentAt?: Record<string, string>;
  inventory: {
    badges: string[];
    items: string[];
  };
  rewardBoxes: {
    bronze: number;
    silver: number;
    gold: number;
    diamond: number;
  };
  vanguardIdentity?: {
    title: string;
  };
  tutorialStep: number;
  isTutorialCompleted: boolean;
  completedStandardModes?: string[];
  adCounters?: {
    heartAdsCount: number;
    lastHeartAdTime: string;
    lastBoxAdTime: string;
    starPracticeAdsCount: number;
    lastAdResetDate: string;
  };
  hasGrantedWelcomeCores?: boolean;
}

const HEART_RESTORE_TIME_FREE = 600; // 10 minutes
const HEART_RESTORE_TIME_PRO = 300;  // 5 minutes
const MAX_HEARTS = 5;

const getMaxHearts = (isPremium: boolean) => MAX_HEARTS;

export function useUser() {
  const [user, setUser] = useState<UserData | null>(null);
  const [loading, setLoading] = useState(true);
  const [isDeletingAccount, setIsDeletingAccount] = useState(() => localStorage.getItem('pending_account_deletion') === 'true');
  const [secondsToNextHeart, setSecondsToNextHeart] = useState<number | null>(null);
  const hasClampedTutorial = useRef(false);
  const isDeletingRef = useRef(false);
  const unsubscribeDocRef = useRef<(() => void) | null>(null);
  const hasAttemptedAutoSignIn = useRef(false);

  // 1. Auth & Snapshot Subscription
  useEffect(() => {
    let unsubscribeDoc: (() => void) | null = null;
    let unsubscribeNativeAuth: (() => void) | null = null;
    let isNativeChecking = Capacitor.isNativePlatform();
    
    // getRedirectResult is web-only — on native Capacitor it's a no-op / can throw
    // Wrapped in async IIFE since useEffect callback cannot be async directly
    (async () => {
      if (!Capacitor.isNativePlatform()) {
        getRedirectResult(auth).then(async (result) => {
          if (result && localStorage.getItem('pending_account_deletion') === 'true') {
            console.log("Redirect re-authentication successful. Finalizing account deletion...");
            if (auth.currentUser) {
              try {
                setIsDeletingAccount(true);
                isDeletingRef.current = true;
                localStorage.setItem('block_auto_signin', 'true');
                if (unsubscribeDocRef.current) {
                  unsubscribeDocRef.current();
                  unsubscribeDocRef.current = null;
                }
                if (unsubscribeDoc) {
                  unsubscribeDoc();
                  unsubscribeDoc = null;
                }
                const uid = auth.currentUser.uid;
                const userDocRef = doc(db, 'users', uid);
                await deleteDoc(userDocRef);
                await deleteUser(auth.currentUser);
                console.log("Account completely removed after redirect re-auth.");
              } catch (deleteErr) {
                console.error("Error finalizing deletion after redirect:", deleteErr);
              } finally {
                localStorage.removeItem('pending_account_deletion');
                localStorage.removeItem('numq_user_has_completed_tutorial');
                sessionStorage.removeItem('nr_current_screen');
                Object.keys(localStorage).forEach(key => {
                  if (key.startsWith('shown_milestone_')) localStorage.removeItem(key);
                });
                setUser(null);
                setLoading(false);
                setIsDeletingAccount(false);
                isDeletingRef.current = false;
              }
            } else {
              localStorage.removeItem('pending_account_deletion');
              setIsDeletingAccount(false);
            }
          } else {
            localStorage.removeItem('pending_account_deletion');
            setIsDeletingAccount(false);
          }
        }).catch(err => {
          console.error("Redirect auth error:", err);
          localStorage.removeItem('pending_account_deletion');
          setIsDeletingAccount(false);
        });
      } else {
        // Initial sync check: On startup, if there is a native user but Web SDK is not logged in,
        // we do NOT trigger automatic sign-in here since Firebase Web SDK onAuthStateChanged
        // automatically restores the authenticated session.
        // Initial sync check: On startup, try silent auto sign-in ONCE
        (async () => {
          if (hasAttemptedAutoSignIn.current) {
            console.log("[useUser] Auto sign-in already attempted, skipping.");
            return;
          }
          hasAttemptedAutoSignIn.current = true;
          console.log("[useUser] Attempting controlled silent auto sign-in on startup...");

          try {
            const { user: nativeUser } = await FirebaseAuthentication.getCurrentUser();
            console.log("[useUser] Startup check. Native user exists:", !!nativeUser);
            
            if (nativeUser) {
              console.log("[useUser] Native user found on launch. Fetching Play Games credentials silently...");
              const result = await FirebaseAuthentication.signInWithPlayGames();
              const serverAuthCode = result.credential?.serverAuthCode || (result as any).serverAuthCode;
              
              if (serverAuthCode) {
                console.log("[useUser] Exchanging serverAuthCode for Custom Token...");
                const functionsInstance = getFunctions(undefined, 'us-central1');
                const exchangePlayGamesAuthCode = httpsCallable<{ serverAuthCode: string }, { success: boolean, customToken: string, displayName?: string, avatarUrl?: string, playerId?: string }>(
                  functionsInstance,
                  'exchangePlayGamesAuthCode'
                );
                const cloudRes = await exchangePlayGamesAuthCode({ serverAuthCode });
                
                if (cloudRes.data && cloudRes.data.customToken) {
                  console.log("[useUser] Custom Token received on launch. Signing in Web SDK...");
                  await signInWithCustomToken(auth, cloudRes.data.customToken);
                  
                  if (auth.currentUser) {
                    const { updateProfile } = await import('firebase/auth');
                    await updateProfile(auth.currentUser, {
                      displayName: cloudRes.data.displayName || null,
                      photoURL: cloudRes.data.avatarUrl || null
                    });
                  }
                  
                  console.log("[useUser] Web SDK successfully signed in.");
                } else {
                  console.warn("[useUser] Cloud Function exchange failed: no token in data");
                  setLoading(false);
                }
              } else {
                console.warn("[useUser] No serverAuthCode received on startup.");
                setLoading(false);
              }
            } else {
              console.log("[useUser] No native user logged in on launch.");
              setLoading(false);
            }
          } catch (err) {
            console.error("[useUser] Controlled silent auto sign-in failed:", err);
            setLoading(false);
          } finally {
            isNativeChecking = false;
          }
        })();

        // Set up authStateChange listener only to handle sign-out synchronization
        const listenerPromise = FirebaseAuthentication.addListener('authStateChange', async (change) => {
          console.log("[useUser] Native authStateChange event fired:", change.user ? `UID: ${change.user.uid}` : "null");
          if (!change.user) {
            if (auth.currentUser) {
              console.log("[useUser] Native user signed out. Signing out Web SDK...");
              await signOut(auth);
            }
          }
        });
        unsubscribeNativeAuth = () => {
          listenerPromise.then(h => h.remove());
        };
      }
    })();

    const unsubscribeAuth = onAuthStateChanged(auth, async (firebaseUser) => {
      console.log("[useUser] onAuthStateChanged:", firebaseUser ? `UID: ${firebaseUser.uid}` : "null");
      if (isDeletingRef.current) {
        console.log("[useUser] Auth change during account deletion. Skipping.");
        if (!firebaseUser) {
          setUser(null);
          setLoading(false);
        }
        return;
      }
      if (unsubscribeDoc) {
        unsubscribeDoc();
        unsubscribeDoc = null;
        unsubscribeDocRef.current = null;
      }
      
      if (firebaseUser) {
        const userDocRef = doc(db, 'users', firebaseUser.uid);
        console.log("[useUser] Subscribing to user doc in Firestore...");
        
        unsubscribeDoc = onSnapshot(userDocRef, {
          next: (docSnap) => {
            console.log("[useUser] Snapshot received. Doc exists:", docSnap.exists());
            if (docSnap.exists()) {
              const data = docSnap.data() as UserData;
              
              // Migrate equippedPrismId to mode-specific equippedPrismIds
              if (!data.equippedPrismIds) {
                const defaultId = data.equippedPrismId || null;
                data.equippedPrismIds = {
                  pemdas: defaultId,
                  shellcrush: defaultId,
                  goldrush: defaultId
                };
                Promise.resolve().then(() => {
                  updateDoc(userDocRef, { equippedPrismIds: data.equippedPrismIds }).catch(console.error);
                });
              }

              // Sync premium flags: isPremium and is_pro_member should be identical
              if (data.isPremium !== data.is_pro_member) {
                const isPro = data.isPremium || data.is_pro_member || false;
                data.isPremium = isPro;
                data.is_pro_member = isPro;
                Promise.resolve().then(() => {
                  updateDoc(userDocRef, { isPremium: isPro, is_pro_member: isPro }).catch(console.error);
                });
              }

              if (!hasClampedTutorial.current && data.tutorialStep > 0 && !data.isTutorialCompleted) {
                let step = data.tutorialStep;
                if (step >= 1 && step <= 4) step = 1;
                else if (step >= 5 && step <= 13) step = 5;
                else if (step >= 14 && step <= 21) step = 14;
                else if (step >= 22 && step <= 28) step = 22;
                else if (step >= 29 && step <= 35) step = 29;
                else if (step >= 36 && step <= 49) step = 36;
                
                if (step !== data.tutorialStep) {
                  Promise.resolve().then(() => {
                    updateDoc(userDocRef, { tutorialStep: step }).catch(console.error);
                  });
                  data.tutorialStep = step;
                }
                hasClampedTutorial.current = true;
              } else if (data.tutorialStep > 0 && !data.isTutorialCompleted) {
                // In case it was already clamped or was loaded directly on a boundary
                hasClampedTutorial.current = true;
              }

              setUser(data);
            } else {
              if (isDeletingRef.current) {
                console.log("Account is being deleted, skipping user recreation.");
                setLoading(false);
                return;
              }
              // Initialize new user
              const now = new Date();
              const currentReset = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), 4, 0, 0, 0));
              if (now < currentReset) currentReset.setUTCDate(currentReset.getUTCDate() - 1);
              const resetAnchor = currentReset.toISOString();

              const newUser: UserData = {
                uid: firebaseUser.uid,
                displayName: firebaseUser.displayName,
                nickname: firebaseUser.isAnonymous ? `Guest#${Math.floor(Math.random() * 90) + 10}` : null,
                photoURL: firebaseUser.photoURL,
                isPremium: false,
                level: 1,
                exp: 0,
                totalExp: 0,
                hearts: getMaxHearts(false),
                coins: 0,
                gems: 0,
                user_credits: 0,
                is_pro_member: false,
                heartTickets: 5,
                prismTickets: 0,
                starTickets: 0,
                animaTickets: {
                  1: 0,
                  2: 0,
                  3: 0
                },
                prismInventory: [],
                equippedPrismId: null,
                equippedPrismIds: { pemdas: null, shellcrush: null, goldrush: null },
                highScores: { pemdas: 0, shellcrush: 0, starburst: 0, goldrush: 0 },
                counters: {
                  lifetimeCorrect: 0, lifetimeGames: 0, dailyGames: 0,
                  monthlyGames: 0, coinsSpent: 0, heartsSent: 0,
                  blocks_destroyed: 0, hearts_consumed: 0, fever_count: 0,
                  total_gc_spent: 0, total_cc_spent: 0,
                  plays_starburst: 0, plays_cloud: 0, plays_gold: 0, plays_shell: 0,
                  animaTicketsOpened: 0
                },
                equippedBadgeId: '',
                inventory: { badges: [], items: [] },
                isAnonymous: firebaseUser.isAnonymous,
                lastHeartRestoreTime: new Date().toISOString(),
                lastTicketSentAt: {},
                lastLoginDate: resetAnchor,
                dailyResetUTC: resetAnchor,
                monthlyResetUTC: startOfMonth(now).toISOString(),
                dailyGift: { lastClaimedDate: null, currentStreak: 0, isClaimedToday: false, isDoubledToday: false },
                proGift: { lastClaimedDate: null, currentDay: 0, isClaimedToday: false },
                rewardBoxes: { bronze: 0, silver: 0, gold: 0, diamond: 0 },
                tutorialStep: 0,
                isTutorialCompleted: false,
                hasGrantedWelcomeCores: false,
                completedStandardModes: [],
                selectedTheme: 'classic',
                unlockedThemes: ['classic'],
                badges: [],
                avatarId: 'g1',
                purchasedAvatars: ['g1'],
                activeMilestoneOffer: null,
                purchasedMilestoneOffers: [],
                expiredMilestoneOffers: [],
                adCounters: {
                  heartAdsCount: 0,
                  lastHeartAdTime: new Date(0).toISOString(),
                  lastBoxAdTime: new Date(0).toISOString(),
                  starPracticeAdsCount: 0,
                  lastAdResetDate: resetAnchor.split('T')[0]
                }
              };
              setDoc(userDocRef, newUser).catch(err => {
                console.error("Error creating user doc:", err);
                window.alert("Database Error: Could not create your profile. Check your Firebase Rules. " + err.message);
              });
              setUser(newUser);
            }
            setLoading(false);
          },
          error: (err) => {
            console.error("Firestore snapshot error:", err);
            setLoading(false);
          }
        });
        unsubscribeDocRef.current = unsubscribeDoc;
      } else {
        setUser(null);
        if (!isNativeChecking) {
          setLoading(false);
        }
      }
    });

    return () => {
      unsubscribeAuth();
      if (unsubscribeNativeAuth) unsubscribeNativeAuth();
      if (unsubscribeDoc) unsubscribeDoc();
      unsubscribeDocRef.current = null;
    };
  }, []);

  // Self-Correction for negative reward boxes
  useEffect(() => {
    if (!user) return;
    const negs = Object.entries(user.rewardBoxes || {}).filter(([_, v]) => (v as number) < 0);
    if (negs.length > 0) {
      const updates: any = {};
      negs.forEach(([k, _]) => {
        updates[`rewardBoxes.${k}`] = 0;
      });
      updateDoc(doc(db, 'users', user.uid), updates).catch(e => console.error("Self-correction failed:", e));
    }
  }, [user?.rewardBoxes, user?.uid]);

  // 2. Resets & Daily Logic
  useEffect(() => {
    if (!user) return;
    const now = new Date();
    const currentReset = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), 4, 0, 0, 0));
    if (now < currentReset) currentReset.setUTCDate(currentReset.getUTCDate() - 1);
    const resetAnchor = currentReset.toISOString();
    const todayLabel = startOfDay(now).toISOString();
    
    const updates: any = {};
    let needsUpdate = false;

    if (user.lastLoginDate !== todayLabel) {
      updates.lastLoginDate = todayLabel;
      needsUpdate = true;
    }

    if (user.dailyResetUTC !== resetAnchor) {
      updates.dailyResetUTC = resetAnchor;
      updates['counters.dailyGames'] = 0;
      updates['dailyGift.isClaimedToday'] = false;
      updates['dailyGift.isDoubledToday'] = false;
      updates['proGift.isClaimedToday'] = false;
      needsUpdate = true;
    }


    const monthAnchor = startOfMonth(now).toISOString();
    if (user.monthlyResetUTC !== monthAnchor) {
      updates.monthlyResetUTC = monthAnchor;
      updates['counters.monthlyGames'] = 0;
      needsUpdate = true;
    }

    if (!user.avatarId || !user.purchasedAvatars) {
      updates.avatarId = user.avatarId || 'a1';
      updates.purchasedAvatars = user.purchasedAvatars || ['a1'];
      needsUpdate = true;
    }

    if (needsUpdate) {
      updateDoc(doc(db, 'users', user.uid), updates).catch(err => console.error("Error updating resets:", err));
    }
  }, [user?.uid, user?.lastLoginDate, user?.dailyResetUTC, user?.monthlyResetUTC]);

  // Achievement Avatar Unlocks (Automatic)
  useEffect(() => {
    if (!user) return;
    
    // Achievement avatars from the catalog
    const milestoneAvatars = [
      { id: 'm1', level: 25 },
      { id: 'm2', level: 50 },
      { id: 'm3', level: 75 },
      { id: 'm4', level: 100 }
    ];
    
    const currentPurchased = user.purchasedAvatars || [];
    const toUnlock: string[] = [];
    
    milestoneAvatars.forEach(ach => {
      if (user.level >= ach.level && !currentPurchased.includes(ach.id)) {
        toUnlock.push(ach.id);
      }
    });

    if (toUnlock.length > 0) {
      const userRef = doc(db, 'users', user.uid);
      updateDoc(userRef, {
        purchasedAvatars: [...currentPurchased, ...toUnlock]
      }).catch(err => console.error("Error unlocking milestone avatars:", err));
    }
  }, [user?.level, user?.purchasedAvatars]);

  // 3. Heart Restoration Timer
  useEffect(() => {
    if (!user) return;
    const maxHearts = getMaxHearts(user.isPremium || false);
    if (user.hearts >= maxHearts) {
      setSecondsToNextHeart(null);
      return;
    }
    const updateTimer = () => {
      const now = new Date();
      const lastRestore = new Date(user.lastHeartRestoreTime);
      const restoreInterval = user.isPremium ? HEART_RESTORE_TIME_PRO : HEART_RESTORE_TIME_FREE;
      const nextRestore = new Date(lastRestore.getTime() + (restoreInterval * 1000));
      const diffSeconds = Math.max(0, Math.floor((nextRestore.getTime() - now.getTime()) / 1000));
      setSecondsToNextHeart(diffSeconds);
    };
    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [user]);

  useEffect(() => {
    if (!user) return;
    const interval = setInterval(async () => {
      const maxHearts = getMaxHearts(user.isPremium || false);
      if (user.hearts < maxHearts) {
        const now = new Date();
        const lastRestore = new Date(user.lastHeartRestoreTime);
        const restoreInterval = user.isPremium ? HEART_RESTORE_TIME_PRO : HEART_RESTORE_TIME_FREE;
        const elapsed = Math.floor((now.getTime() - lastRestore.getTime()) / 1000);
        
        if (elapsed >= restoreInterval) {
          const heartsToAdd = Math.floor(elapsed / restoreInterval);
          const newHearts = Math.min(MAX_HEARTS, user.hearts + heartsToAdd);
          const newRestoreTime = new Date(lastRestore.getTime() + (heartsToAdd * restoreInterval * 1000));
          
          await updateDoc(doc(db, 'users', user.uid), {
            hearts: newHearts,
            lastHeartRestoreTime: newRestoreTime.toISOString()
          });
        }
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [user]);

  // 4. Core Actions
  const consumeHeart = async () => {
    if (!user || user.hearts <= 0) return false;

    const maxHearts = getMaxHearts(user.isPremium);
    const updateData: any = { 
      hearts: user.hearts - 1,
      'counters.hearts_consumed': increment(1)
    };
    if (user.hearts === maxHearts) updateData.lastHeartRestoreTime = new Date().toISOString();
    await updateDoc(doc(db, 'users', user.uid), updateData);
    return true;
  };

  const consumeStarTicket = async () => {
    if (!user || (user.starTickets || 0) <= 0) return false;
    await updateDoc(doc(db, 'users', user.uid), { starTickets: increment(-1) });
    return true;
  };

  const handleGameEnd = async (gameStats: {
    score: number; rawScore: number; correctAnswers: number; mistakes: number;
    feverAnswers: number; mode: 'pemdas' | 'shellcrush' | 'starburst' | 'goldrush';
    bonusExp?: number;
  }) => {
    if (!user) return null;
    
    const activePrismId = user.equippedPrismIds?.[gameStats.mode] || user.equippedPrismId;
    const equippedPrism = user.prismInventory.find(p => p.id === activePrismId);
    const buffs = gameStats.mode === 'starburst' ? calculatePrismBuffs(undefined) : calculatePrismBuffs(equippedPrism);

    let earnedExp = Math.floor(calculateExp(gameStats) * (1 + buffs.expMultiplier) + buffs.flatExp) + (gameStats.bonusExp || 0);
    
    // Star Burst Massive Rewards
    // Star Burst Massive Rewards
    if (gameStats.mode === 'starburst') {
      earnedExp = gameStats.score * 1000;
    }

    let earnedCoins = Math.floor(gameStats.rawScore * 0.1 * (1 + buffs.coinMultiplier) + buffs.flatCoins);
    let earnedGems = 0;

    if (gameStats.mode === 'starburst') {
      earnedCoins = 0;
      earnedGems = gameStats.score * 10;
    }

    let newExp = (user.exp || 0) + earnedExp;
    let newLevel = user.level || 1;
    let leveledUp = false;
    const rewards: any[] = [];
    const levelUps: { level: number, rewards: any[] }[] = [];
    
    let overflowCoins = 0;
    if (newLevel >= 100) {
      overflowCoins = earnedExp * 2;
      earnedCoins += overflowCoins;
      earnedExp = 0;
      newExp = 0;
    } else {
      while (newLevel < 100 && newExp >= getExpToNextLevel(newLevel)) {
        newExp -= getExpToNextLevel(newLevel);
        newLevel++;
        leveledUp = true;
        const levelRewards = await awardRewards(newLevel);
        rewards.push(...levelRewards);
        levelUps.push({ level: newLevel, rewards: levelRewards });
      }
      if (newLevel >= 100) {
        overflowCoins = newExp * 2;
        earnedCoins += overflowCoins;
        newExp = 0;
      }
    }

    if (overflowCoins > 0) {
      rewards.push({ type: 'coins', amount: overflowCoins, isMaxLevelBonus: true });
    }

    const updateData: any = {
      level: newLevel, 
      exp: newExp, 
      totalExp: (user.totalExp || 0) + earnedExp,
      coins: increment(earnedCoins),
      gems: increment(earnedGems),
      'counters.lifetimeCorrect': increment(gameStats.correctAnswers),
      'counters.blocks_destroyed': increment(gameStats.correctAnswers),
      'counters.lifetimeGames': increment(1),
      'counters.dailyGames': increment(1),
      'counters.monthlyGames': increment(1),
      'counters.fever_count': increment((gameStats as any).feverTriggerCount || 0),
    };

    // Marathon Updates
    if (gameStats.mode === 'starburst') updateData['counters.plays_starburst'] = increment(1);
    else if (gameStats.mode === 'pemdas') updateData['counters.plays_cloud'] = increment(1);
    else if (gameStats.mode === 'goldrush') updateData['counters.plays_gold'] = increment(1);
    else if (gameStats.mode === 'shellcrush') updateData['counters.plays_shell'] = increment(1);

    if (gameStats.score > (user.highScores?.[gameStats.mode] || 0)) {
      updateData[`highScores.${gameStats.mode}`] = gameStats.score;
    }
    if (leveledUp) updateData.hearts = getMaxHearts(user.isPremium || false);
    await updateDoc(doc(db, 'users', user.uid), updateData);
    return { leveledUp, newLevel, earnedExp, earnedCoins, earnedGems, rewards, levelUps };
  };

  const awardRewards = async (level: number) => {
    if (!user) return [];
    // Base level up reward: 5000 GC and 50 CC
    const rewards: any[] = [
      { type: 'coins', amount: 5000 },
      { type: 'gems', amount: 50 }
    ];

    // Star Ticket Rewards Logic
    if (level >= 10 && level <= 50 && level % 5 === 0) {
      rewards.push({ type: 'star_tickets', amount: 1 });
    } else if (level >= 51 && level <= 75) {
      rewards.push({ type: 'star_tickets', amount: 1 });
    } else if (level >= 76) {
      rewards.push({ type: 'star_tickets', amount: 2 });
    }
    
    const updateData: any = {};
    let addedCoins = 0; let addedGems = 0; let addedTickets = 0; let addedStarTickets = 0;
    rewards.forEach(r => {
      if (r.type === 'coins') addedCoins += r.amount;
      if (r.type === 'gems') addedGems += r.amount;
      if (r.type === 'tickets') addedTickets += r.amount;
      if (r.type === 'star_tickets') addedStarTickets += r.amount;
    });
    
    if (addedCoins > 0) updateData.coins = increment(addedCoins);
    if (addedGems > 0) updateData.gems = increment(addedGems);
    if (addedTickets > 0) updateData.heartTickets = increment(addedTickets);
    if (addedStarTickets > 0) updateData.starTickets = increment(addedStarTickets);
    
    // Milestone Offer Trigger (Queue system)
    const purchased = user.purchasedMilestoneOffers || [];
    const expired = user.expiredMilestoneOffers || [];
    
    // Auto-expire current if passed its date
    let currentActive = user.activeMilestoneOffer;
    if (currentActive && new Date(currentActive.expiresAt).getTime() <= Date.now()) {
      expired.push(currentActive.id);
      currentActive = null;
      updateData.activeMilestoneOffer = null;
      updateData.expiredMilestoneOffers = expired;
    }

    if (!currentActive) {
      // Find the lowest unlocked level offer that hasn't been bought or expired
      const availableOffer = MILESTONE_OFFERS
        .sort((a, b) => a.unlockLevel - b.unlockLevel)
        .find(o => level >= o.unlockLevel && !purchased.includes(o.id) && !expired.includes(o.id));
        
      if (availableOffer) {
        const expiresAt = new Date(Date.now() + availableOffer.durationHours * 60 * 60 * 1000).toISOString();
        updateData.activeMilestoneOffer = {
          id: availableOffer.id,
          expiresAt
        };
        if (updateData.expiredMilestoneOffers === undefined) {
          updateData.expiredMilestoneOffers = expired;
        }
      }
    }
    
    if (Object.keys(updateData).length > 0) await updateDoc(doc(db, 'users', user.uid), updateData);
    return rewards;
  };

  const addSpecificBadge = async (badgeId: string) => {
    if (!user) return;
    const currentBadges = user.inventory?.badges || [];
    if (!currentBadges.includes(badgeId)) {
      await updateDoc(doc(db, 'users', user.uid), {
        "inventory.badges": [...currentBadges, badgeId],
        equippedBadgeId: badgeId
      });
    }
  };

  const addExp = async (amount: number) => {
    if (!user) return null;
    
    let earnedExp = amount;
    let newExp = (user.exp || 0) + earnedExp;
    let newLevel = user.level || 1;
    let leveledUp = false;
    const rewards: any[] = [];
    const levelUps: { level: number, rewards: any[] }[] = [];

    let overflowCoins = 0;
    if (newLevel >= 100) {
      overflowCoins = earnedExp * 2;
      newExp = 0;
    } else {
      while (newLevel < 100 && newExp >= getExpToNextLevel(newLevel)) {
        newExp -= getExpToNextLevel(newLevel);
        newLevel++;
        leveledUp = true;
        const levelRewards = await awardRewards(newLevel);
        rewards.push(...levelRewards);
        levelUps.push({ level: newLevel, rewards: levelRewards });
      }
      if (newLevel >= 100) {
        overflowCoins = newExp * 2;
        newExp = 0;
      }
    }

    const updateData: any = {
      exp: newExp,
      level: newLevel,
      totalExp: increment(amount)
    };

    if (overflowCoins > 0) {
      updateData.coins = increment(overflowCoins);
      rewards.push({ type: 'coins', amount: overflowCoins, isMaxLevelBonus: true });
    }

    if (leveledUp) {
      updateData.hearts = getMaxHearts(user.isPremium || false);
    }

    await updateDoc(doc(db, 'users', user.uid), updateData);
    return { leveledUp, newLevel, rewards, levelUps };
  };

  const restoreOneHeart = async () => {
    if (!user) return;
    await updateDoc(doc(db, 'users', user.uid), { hearts: user.hearts + 1 });
  };

  const updateNickname = async (nickname: string) => {
    if (!user) return { success: false, error: 'User not found' };
    if (user.isAnonymous) return { success: false, error: 'GUESTS_CANNOT_CHANGE_NAME' };
    const validation = validateNickname(nickname);
    if (!validation.isValid) return { success: false, error: validation.error };
    const q = query(collection(db, 'users'), where('nickname', '==', nickname));
    const querySnapshot = await getDocs(q);
    if (querySnapshot.docs.some(doc => doc.id !== user.uid)) return { success: false, error: 'NAME_TAKEN' };
    await updateDoc(doc(db, 'users', user.uid), { nickname });
    return { success: true };
  };

  const addCoins = async (amount: number) => {
    if (!user) return;
    await updateDoc(doc(db, 'users', user.uid), { coins: increment(amount) });
  };

  const addGems = async (amount: number) => {
    if (!user) return;
    await updateDoc(doc(db, 'users', user.uid), { gems: increment(amount) });
  };

  const buyTicket = async () => {
    if (!user || user.coins < 2000) return false;
    await updateDoc(doc(db, 'users', user.uid), { 
      coins: increment(-2000), 
      heartTickets: increment(1),
      'counters.total_gc_spent': increment(2000)
    });
    return true;
  };

  const buyStarTicket = async () => {
    if (!user || user.heartTickets < 20) return false;
    await updateDoc(doc(db, 'users', user.uid), { 
      heartTickets: increment(-20), 
      starTickets: increment(1)
    });
    return true;
  };

  const redeemTicket = async () => {
    if (!user || user.heartTickets <= 0) return false;
    await updateDoc(doc(db, 'users', user.uid), { heartTickets: increment(-1), hearts: increment(1) });
    return true;
  };

  const openAnimaTickets = async (ticketStars: 1 | 2 | 3, count: number): Promise<Prism[] | null> => {
    if (!user) return null;
    const owned = user.animaTickets?.[ticketStars] || 0;
    if (owned < count || count <= 0) return null;
    // Roll the outcomes and generate prisms
    const newPrisms: Prism[] = [];
    for (let i = 0; i < count; i++) {
      const rolledStars = rollAnimaTicketStars(ticketStars);
      const newPrism = generatePrism(rolledStars);
      newPrisms.push(newPrism);
    }

    const currentInventory = user.prismInventory || [];
    const nextInv = [...currentInventory, ...newPrisms];

    await updateDoc(doc(db, 'users', user.uid), {
      [`animaTickets.${ticketStars}`]: increment(-count),
      prismInventory: nextInv,
      'counters.animaTicketsOpened': increment(count)
    });

    return newPrisms;
  };

  const refillHearts = async () => {
    if (!user || user.gems < 25) return false;
    await updateDoc(doc(db, 'users', user.uid), { 
      gems: increment(-25), 
      hearts: 5,
      'counters.total_cc_spent': increment(25)
    });
    return true;
  };

  const grantMilestoneOfferRewards = async (offerId: string) => {
    if (!user) return false;
    const offer = MILESTONE_OFFERS.find(o => o.id === offerId);
    if (!offer) return false;

    const purchased = user.purchasedMilestoneOffers || [];
    if (purchased.includes(offerId)) return false;

    const updates: any = {
      purchasedMilestoneOffers: [...purchased, offerId],
      activeMilestoneOffer: null
    };

    // Add Coins & Gems
    updates.coins = increment(offer.rewards.coins);
    updates.gems = increment(offer.rewards.gems);

    // Add Star Tickets
    if (offer.rewards.starTickets > 0) {
      updates.starTickets = increment(offer.rewards.starTickets);
    }

    // Add Diamond Boxes
    if (offer.rewards.boxes.diamond > 0) {
      updates['rewardBoxes.diamond'] = increment(offer.rewards.boxes.diamond);
    }

    // Generate and inject 4-Star Prisms (Anima Cores)
    if (offer.rewards.prisms.count > 0) {
      const newPrisms = Array.from({ length: offer.rewards.prisms.count }).map(() => 
        generatePrism(offer.rewards.prisms.stars)
      );
      updates.prismInventory = [...(user.prismInventory || []), ...newPrisms];
    }

    await updateDoc(doc(db, 'users', user.uid), updates);
    return true;
  };

  const expireMilestoneOffer = async (offerId: string) => {
    if (!user) return;
    const expired = user.expiredMilestoneOffers || [];
    if (expired.includes(offerId)) return;

    await updateDoc(doc(db, 'users', user.uid), {
      activeMilestoneOffer: null,
      expiredMilestoneOffers: [...expired, offerId]
    });
  };

  const sendHeartTicket = async (friendUid: string, message: string = '') => {
    if (!user) return false;
    const lastSent = user.lastTicketSentAt?.[friendUid];
    if (lastSent && (new Date().getTime() - new Date(lastSent).getTime()) < 6 * 60 * 60 * 1000) return false;
    const friendSnap = await getDoc(doc(db, 'users', friendUid));
    if (friendSnap.exists()) {
      await addDoc(collection(db, 'users', friendUid, 'gifts'), {
        fromUid: user.uid, fromName: user.nickname || user.displayName || 'Anonymous',
        message, timestamp: new Date().toISOString(), claimed: false
      });
      await updateDoc(doc(db, 'users', user.uid), { [`lastTicketSentAt.${friendUid}`]: new Date().toISOString() });
      return true;
    }
    return false;
  };

  const claimGift = async (giftId: string) => {
    if (!user) return false;
    const giftRef = doc(db, 'users', user.uid, 'gifts', giftId);
    const giftSnap = await getDoc(giftRef);
    if (giftSnap.exists() && !giftSnap.data().claimed) {
      await updateDoc(giftRef, { claimed: true });
      await updateDoc(doc(db, 'users', user.uid), { heartTickets: increment(1) });
      return true;
    }
    return false;
  };

  const claimDailyGift = async (track: 'basic' | 'premium', double: boolean = false) => {
    if (!user) return null;
    
    const isBasic = track === 'basic';
    const trackState = isBasic ? user.dailyGift : user.proGift;
    
    if (trackState.isClaimedToday && !double) return null;
    if (isBasic && user.dailyGift.isDoubledToday) return null;
    
    // Premium track requires active membership and cannot be doubled
    if (!isBasic && (double || !user.isPremium)) return null;

    const isClaimed = trackState.isClaimedToday;
    
    // If doubling and NOT yet claimed, we give 2x.
    // If doubling and ALREADY claimed, we give 1x more (legacy/fallback).
    const streakToAward = isClaimed ? 
      (isBasic ? user.dailyGift.currentStreak : user.proGift.currentDay) : 
      ((isBasic ? user.dailyGift.currentStreak : user.proGift.currentDay) % 28 + 1);
      
    const dayData = DAILY_REWARD_TRACK[streakToAward - 1];
    
    const updateData: any = {};
    if (isBasic) {
      updateData['dailyGift.lastClaimedDate'] = new Date().toISOString().split('T')[0];
      if (!isClaimed) updateData['dailyGift.currentStreak'] = streakToAward;
      updateData['dailyGift.isClaimedToday'] = true;
      updateData['dailyGift.isDoubledToday'] = double;
    } else {
      updateData['proGift.lastClaimedDate'] = new Date().toISOString().split('T')[0];
      if (!isClaimed) updateData['proGift.currentDay'] = streakToAward;
      updateData['proGift.isClaimedToday'] = true;
    }

    const combinedRewards: SingleReward[] = [];

    const applyIndividualReward = (r: SingleReward, isPremiumReward: boolean = false) => {
      // Create a copy to avoid modifying the original track data
      const rewardCopy = { ...r };
      
      if (rewardCopy.type === 'prism') {
        const stars = rewardCopy.stars || 1;
        updateData[`animaTickets.${stars}`] = increment(rewardCopy.amount);
      }

      combinedRewards.push({ ...rewardCopy, isPremium: isPremiumReward } as any);
      if (rewardCopy.type === 'coins') updateData.coins = increment(rewardCopy.amount);
      if (rewardCopy.type === 'gems') updateData.gems = increment(rewardCopy.amount);
      if (rewardCopy.type === 'tickets') {
        if (rewardCopy.subType === 'star') {
          updateData.starTickets = increment(rewardCopy.amount);
        } else {
          updateData.heartTickets = increment(rewardCopy.amount);
        }
      }

      if (rewardCopy.type === 'box' && rewardCopy.subType) {
        updateData[`rewardBoxes.${rewardCopy.subType}`] = increment(rewardCopy.amount);
      }
    };

    const applyDailyReward = (reward: DailyReward, isPremiumReward: boolean = false) => {
      if (Array.isArray(reward)) {
        reward.forEach(r => applyIndividualReward(r, isPremiumReward));
      } else {
        applyIndividualReward(reward, isPremiumReward);
      }
    };

    // New logic: If double is requested and we haven't claimed yet, apply rewards TWICE.
    const multiplier = (double && !isClaimed) ? 2 : 1;
    
    for (let i = 0; i < multiplier; i++) {
      if (isBasic) {
        applyDailyReward(dayData.free, false);
      } else {
        applyDailyReward(dayData.premium, true);
      }
    }

    await updateDoc(doc(db, 'users', user.uid), updateData);
    
    return { 
      type: combinedRewards.length > 1 ? 'Multi' : combinedRewards[0]?.type || 'none',
      amount: combinedRewards.length > 1 ? 0 : combinedRewards[0]?.amount || 0,
      isDouble: double,
      isPremiumReward: !isBasic,
      rewards: combinedRewards
    };
  };


  const unboxPrism = async () => {
    if (!user || user.prismTickets <= 0) return null;
    const newPrism = generatePrism(1); // 1-Star Prism
    await updateDoc(doc(db, 'users', user.uid), {
      prismTickets: increment(-1),
      prismInventory: [...(user.prismInventory || []), newPrism]
    });
    return newPrism;
  };

  const grantTutorialPrisms = async () => {
    if (!user) return;
    setUser(prev => prev ? { ...prev, hasGrantedWelcomeCores: true } : null);
    await updateDoc(doc(db, 'users', user.uid), {
      hasGrantedWelcomeCores: true
    });
  };

  const equipPrism = async (id: string | null, mode?: 'all' | 'pemdas' | 'shellcrush' | 'goldrush') => {
    if (!user) return;
    
    if (!mode || mode === 'all') {
      await updateDoc(doc(db, 'users', user.uid), { 
        equippedPrismId: id,
        equippedPrismIds: {
          pemdas: id,
          shellcrush: id,
          goldrush: id
        }
      });
    } else {
      await updateDoc(doc(db, 'users', user.uid), {
        [`equippedPrismIds.${mode}`]: id
      });
    }
  };

  const isEvolvingRef = useRef(false);

  const evolvePrismsAction = async (prismIds: string[]) => {
    if (isEvolvingRef.current) return null;
    isEvolvingRef.current = true;
    try {
      console.log("Hook: evolvePrismsAction called with:", prismIds);
    if (!user || prismIds.length !== 3) {
      console.log("Hook: Invalid state for evolution:", { hasUser: !!user, idCount: prismIds.length });
      return null;
    }
    const fodder = user.prismInventory.filter(p => prismIds.includes(p.id));
    console.log("Hook: Found fodder count:", fodder.length);
    if (fodder.length !== 3) return null;
    
    const baseStars = fodder[0].stars;
    if (baseStars >= 3) {
      console.error("Hook: Maximum evolution tier reached. Cannot evolve further.");
      return null;
    }
    let cost = 15000;
    if (baseStars === 1) cost = 2500;
    else if (baseStars === 2) cost = 10000;
    else if (baseStars === 3) cost = 15000;

    if (user.coins < cost) {
      console.error("Hook: Not enough coins for evolution. Required:", cost, "Has:", user.coins);
      return null;
    }

    const upgraded = evolvePrism(fodder[0], fodder[1], fodder[2]);
    const nextInv = user.prismInventory.filter(p => !prismIds.includes(p.id));
    nextInv.push(upgraded);
    
    await updateDoc(doc(db, 'users', user.uid), { 
      prismInventory: nextInv,
      equippedPrismId: user.equippedPrismId && prismIds.includes(user.equippedPrismId) ? upgraded.id : user.equippedPrismId,
      'equippedPrismIds.pemdas': user.equippedPrismIds?.pemdas && prismIds.includes(user.equippedPrismIds.pemdas) ? upgraded.id : user.equippedPrismIds?.pemdas || null,
      'equippedPrismIds.shellcrush': user.equippedPrismIds?.shellcrush && prismIds.includes(user.equippedPrismIds.shellcrush) ? upgraded.id : user.equippedPrismIds?.shellcrush || null,
      'equippedPrismIds.goldrush': user.equippedPrismIds?.goldrush && prismIds.includes(user.equippedPrismIds.goldrush) ? upgraded.id : user.equippedPrismIds?.goldrush || null,
      coins: increment(-cost),
      'counters.total_gc_spent': increment(cost)
    });
    return upgraded;
    } finally {
      isEvolvingRef.current = false;
    }
  };

  const rerollPrismAction = async (prismId: string, fodderIds: string[]) => {
    if (!user) return null;
    const target = user.prismInventory.find(p => p.id === prismId);
    if (!target) return null;
    if (target.stars !== 3) {
      console.error("Reroll is only possible for 3-star Anima Cores.");
      return null;
    }

    const fodders = user.prismInventory.filter(p => fodderIds.includes(p.id));
    if (fodders.length !== fodderIds.length) {
      console.error("Some fodder cores are missing.");
      return null;
    }
    if (fodders.some(f => f.stars !== 3)) {
      console.error("All fodder cores must be 3-star.");
      return null;
    }
    const equippedIds = [
      user.equippedPrismId,
      user.equippedPrismIds?.pemdas,
      user.equippedPrismIds?.shellcrush,
      user.equippedPrismIds?.goldrush
    ].filter(Boolean);

    if (fodders.some(f => f.locked || equippedIds.includes(f.id))) {
      console.error("Fodder cores cannot be locked or equipped.");
      return null;
    }

    const sameColorCount = fodders.filter(f => f.color === target.color).length;
    const diffColorCount = fodders.filter(f => f.color !== target.color).length;

    if (sameColorCount === 1 && diffColorCount === 0 && fodders.length === 1) {
      // Valid: 1 same-color fodder
    } else if (sameColorCount === 0 && diffColorCount === 2 && fodders.length === 2) {
      // Valid: 2 different-color fodder
    } else {
      console.error("Invalid fodder selection. Needs 1 same-color or 2 different-color 3-star cores.");
      return null;
    }

    const rerolled = rerollPrismLogic(target, target.stars);
    return { current: rerolled, old: target };
  };

  const commitReroll = async (prismId: string, fodderIds: string[], kept: Prism) => {
    if (!user) return;
    const nextInv = user.prismInventory.filter(p => !fodderIds.includes(p.id));
    const idx = nextInv.findIndex(p => p.id === prismId);
    if (idx !== -1) {
      nextInv[idx] = kept;
    }
    await updateDoc(doc(db, 'users', user.uid), { prismInventory: nextInv });
  };

  const augmentPrismAction = async (prismId: string) => {
    if (!user) return null;
    // Check if user has a Black Prism
    const blackPrismIdx = user.inventory.items.indexOf('black_prism');
    if (blackPrismIdx === -1) return null;

    const target = user.prismInventory.find(p => p.id === prismId);
    if (!target || target.stars < 3) return null;

    const newStats = generateBlackStats();
    const augmented = { ...target, blackStats: newStats };

    if (!target.blackStats) {
      // First time: Apply immediately and consume item
      const nextInv = [...user.prismInventory];
      const idx = nextInv.findIndex(p => p.id === prismId);
      nextInv[idx] = augmented;
      
      const nextItems = [...user.inventory.items];
      nextItems.splice(blackPrismIdx, 1);

      await updateDoc(doc(db, 'users', user.uid), { 
        prismInventory: nextInv,
        'inventory.items': nextItems
      });
      return { current: augmented, old: target, isAutomatic: true };
    }

    // Subsequent: Standoff UI triggered
    return { current: augmented, old: target, isAutomatic: false };
  };

  const commitAugment = async (prismId: string, kept: Prism) => {
    if (!user) return;
    const blackPrismIdx = user.inventory.items.indexOf('black_prism');
    if (blackPrismIdx === -1) return;

    const nextInv = [...user.prismInventory];
    const idx = nextInv.findIndex(p => p.id === prismId);
    nextInv[idx] = kept;
    
    const nextItems = [...user.inventory.items];
    nextItems.splice(blackPrismIdx, 1);

    await updateDoc(doc(db, 'users', user.uid), { 
      prismInventory: nextInv,
      'inventory.items': nextItems
    });
  };

  const addRewards = async (rewards: Reward[]) => {
    if (!user) return;
    const updates: any = {};
    
    // Track local shifts to avoid stale state in the batch
    let currentItems = [...(user.inventory?.items || [])];
    const totalAnimaTickets: Record<number, number> = { 1: 0, 2: 0, 3: 0 };

    for (const reward of rewards) {
      switch (reward.type) {
        case 'coin_stack': 
          updates.coins = increment((updates.coins?._increment || 0) + reward.amount); 
          break;
        case 'starter_bolt': 
          updates.gems = increment((updates.gems?._increment || 0) + reward.amount); 
          break;
        case 'energy_pack': 
          updates.heartTickets = increment((updates.heartTickets?._increment || 0) + reward.amount); 
          break;
        case 'prism': {
          const stars = reward.stars || 1;
          totalAnimaTickets[stars] = (totalAnimaTickets[stars] || 0) + reward.amount;
          break;
        }
        case 'black_prism': {
          currentItems = [...currentItems, 'black_prism'];
          updates['inventory.items'] = currentItems;
          break;
        }
        case 'star_ticket': {
          updates.starTickets = increment((updates.starTickets?._increment || 0) + reward.amount);
          break;
        }
      }
    }
    
    // Normalize increments (Firebase specific)
    let totalCoins = 0, totalGems = 0, totalTickets = 0, totalStarTickets = 0;
    rewards.forEach(r => {
      if (r.type === 'coin_stack') totalCoins += r.amount;
      if (r.type === 'starter_bolt') totalGems += r.amount;
      if (r.type === 'energy_pack') totalTickets += r.amount;
      if (r.type === 'star_ticket') totalStarTickets += r.amount;
    });

    const finalUpdates: any = {};
    if (totalCoins > 0) finalUpdates.coins = increment(totalCoins);
    if (totalGems > 0) finalUpdates.gems = increment(totalGems);
    if (totalTickets > 0) finalUpdates.heartTickets = increment(totalTickets);
    if (totalStarTickets > 0) finalUpdates.starTickets = increment(totalStarTickets);
    if (totalAnimaTickets[1] > 0) finalUpdates['animaTickets.1'] = increment(totalAnimaTickets[1]);
    if (totalAnimaTickets[2] > 0) finalUpdates['animaTickets.2'] = increment(totalAnimaTickets[2]);
    if (totalAnimaTickets[3] > 0) finalUpdates['animaTickets.3'] = increment(totalAnimaTickets[3]);
    if (updates['inventory.items']) finalUpdates['inventory.items'] = updates['inventory.items'];

    if (Object.keys(finalUpdates).length > 0) {
      await updateDoc(doc(db, 'users', user.uid), finalUpdates);
    }
  };

  const addTickets = async (amount: number, type: 'heart_tickets' | 'star_tickets' = 'heart_tickets') => {
    if (!user) return;
    const field = type === 'star_tickets' ? 'starTickets' : 'heartTickets';
    await updateDoc(doc(db, 'users', user.uid), { [field]: increment(amount) });
  };

  const addBadge = async (badgeId: string) => {
    if (!user) return;
    await updateDoc(doc(db, 'users', user.uid), {
      badges: (user as any).badges ? (user as any).badges + 1 : 1
    });
  };

  const addRewardBox = async (tier: keyof UserData['rewardBoxes']) => {
    if (!user) return;
    await updateDoc(doc(db, 'users', user.uid), { [`rewardBoxes.${tier}`]: increment(1) });
  };

  const buyRewardBox = async (tier: keyof UserData['rewardBoxes'], costType: 'coins' | 'gems' | 'ad', costAmount: number, amount: number = 1) => {
    if (!user) return false;
    if (amount <= 0) return false;

    try {
      const updates: any = { [`rewardBoxes.${tier}`]: increment(amount) };
      const totalCost = costAmount * amount;

      if (costType === 'coins') {
        if (user.coins < totalCost) return false;
        updates.coins = increment(-totalCost);
        updates['counters.total_gc_spent'] = increment(totalCost);
      } else if (costType === 'gems') {
        if (user.gems < totalCost) return false;
        updates.gems = increment(-totalCost);
        updates['counters.total_cc_spent'] = increment(totalCost);
      } else if (costType !== 'ad') {
        return false;
      }

      await updateDoc(doc(db, 'users', user.uid), updates);
      console.log(`Successfully bought ${amount} ${tier} boxes`);
      return true;
    } catch (err) {
      console.error("Error buying reward box:", err);
      return false;
    }
  };

  const openRewardBox = async (tier: keyof UserData['rewardBoxes'], count: number = 1) => {
    if (!user) return;
    const owned = user.rewardBoxes?.[tier] || 0;
    if (owned < count || count <= 0) {
      console.warn(`Attempted to open ${count} ${tier} boxes but only owned ${owned}`);
      return;
    }

    try {
      await updateDoc(doc(db, 'users', user.uid), { 
        [`rewardBoxes.${tier}`]: increment(-count) 
      });
      console.log(`Successfully opened ${count} ${tier} boxes`);
    } catch (err) {
      console.error("Error opening reward box:", err);
    }
  };


  const setPremiumStatus = async (status: boolean, forceDirect?: boolean) => {
    if (!user) return;
    if (status && purchaseManager.isAndroid() && !forceDirect) {
      try {
        await purchaseManager.subscribePro();
      } catch (error) {
        console.error("Failed to purchase subscription via Google Play:", error);
      }
      return;
    }
    const updates: any = {
      isPremium: status,
      is_pro_member: status
    };
    if (status) {
      updates.subscription_expiry = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString();
    } else {
      updates.subscription_expiry = null;
    }
    await updateDoc(doc(db, 'users', user.uid), updates);
  };

  const updateTutorialStep = useCallback(async (step: number) => {
    if (!user) return;
    setUser(prev => prev ? { ...prev, tutorialStep: step } : null);
    await updateDoc(doc(db, 'users', user.uid), { tutorialStep: step });
  }, [user]);

  const completeTutorial = useCallback(async () => {
    if (!user) return;
    
    const updates: any = {
      isTutorialCompleted: true,
      tutorialStep: 999
    };
    
    setUser(prev => prev ? { ...prev, isTutorialCompleted: true, tutorialStep: 999 } : null);
    await updateDoc(doc(db, 'users', user.uid), updates);
  }, [user]);

  const signInWithGoogle = async (): Promise<{ success: boolean; error?: string }> => {
    try {
      if (!Capacitor.isNativePlatform()) {
        // This app is Android-only — web sign-in via popup/redirect is intentionally
        // not supported (Google blocks it inside app WebViews with error code 4).
        throw new Error("Sign-in is only supported on the native Android app.");
      }
      // Request credentials from the Play Games native plugin.
      console.log("[signInWithGoogle] Calling signInWithPlayGames...");
      const result = await FirebaseAuthentication.signInWithPlayGames();
      console.log("[signInWithGoogle] Play Games result:", JSON.stringify(result));
 
      const serverAuthCode = result.credential?.serverAuthCode || (result as any).serverAuthCode;
      if (!serverAuthCode) {
        throw new Error("Play Games did not return a serverAuthCode.");
      }

      console.log("[signInWithGoogle] Exchanging Play Games serverAuthCode for Custom Token via Cloud Function on region us-central1...");
      const functionsInstance = getFunctions(undefined, 'us-central1');
      const exchangePlayGamesAuthCode = httpsCallable<{ serverAuthCode: string }, { success: boolean, customToken: string, displayName?: string, avatarUrl?: string, playerId?: string }>(
        functionsInstance,
        'exchangePlayGamesAuthCode'
      );
      const cloudRes = await exchangePlayGamesAuthCode({ serverAuthCode });
      if (cloudRes.data && cloudRes.data.customToken) {
        console.log("[signInWithGoogle] Custom Token received. Signing in Web SDK...");
        await signInWithCustomToken(auth, cloudRes.data.customToken);
        
        if (auth.currentUser) {
          const { updateProfile } = await import('firebase/auth');
          await updateProfile(auth.currentUser, {
            displayName: cloudRes.data.displayName || null,
            photoURL: cloudRes.data.avatarUrl || null
          });
        }
        
        console.log("[signInWithGoogle] Web SDK sync complete.");
        return { success: true };
      } else {
        throw new Error("Cloud Function did not return a customToken.");
      }

    } catch (error: any) {
      console.error("[signInWithGoogle] Play Games auth failed:", error);

      let detailStr = "";
      try {
        const detailObj: any = {};
        Object.getOwnPropertyNames(error).forEach(key => { detailObj[key] = error[key]; });
        detailStr = JSON.stringify(detailObj);
      } catch {
        detailStr = String(error);
      }

      const errorMsg = error?.message || String(error);
      const errorCode = error?.code ? ` (Code: ${error.code})` : "";
      return {
        success: false,
        error: `Play Games connection failed: ${errorMsg}${errorCode} — ${detailStr}`
      };
    }
  };


  const togglePrismLock = async (id: string) => {
    if (!user) return;
    const nextInv = user.prismInventory.map(p => p.id === id ? { ...p, locked: !p.locked } : p);
    await updateDoc(doc(db, 'users', user.uid), { prismInventory: nextInv });
  };

  const deleteAccount = async () => {
    if (!auth.currentUser) return;
    setIsDeletingAccount(true);
    isDeletingRef.current = true;
    
    try {
      // Force refresh the auth token first to guarantee deletion requests are authorized and fresh
      await auth.currentUser.getIdToken(true);
    } catch (tokenErr) {
      console.warn("Failed to refresh ID token before deletion:", tokenErr);
    }

    const uid = auth.currentUser.uid;
    const userDocRef = doc(db, 'users', uid);
    const backupUserData = user;

    // For anonymous users, bypass re-auth redirect loop
    if (auth.currentUser.isAnonymous) {
      try {
        if (unsubscribeDocRef.current) {
          unsubscribeDocRef.current();
          unsubscribeDocRef.current = null;
        }
        await deleteDoc(userDocRef);
        try {
          await deleteUser(auth.currentUser);
        } catch (authErr) {
          console.warn("Auth deleteUser failed for anonymous user, signing out:", authErr);
          await signOut(auth);
        }
        if (Capacitor.isNativePlatform()) {
          try {
            await FirebaseAuthentication.signOut();
          } catch (signOutErr) {
            console.error("Native sign out failed during anonymous deleteAccount:", signOutErr);
          }
        }
        setUser(null);
        setLoading(false);
        setIsDeletingAccount(false);
        isDeletingRef.current = false;
      } catch (err) {
        setIsDeletingAccount(false);
        isDeletingRef.current = false;
        throw err;
      }
      return;
    }

    // Mark pending deletion for Google users in case redirect occurs
    localStorage.setItem('pending_account_deletion', 'true');
    localStorage.setItem('block_auto_signin', 'true');

    const reauthenticateUser = async () => {
      const currentUser = auth.currentUser;
      if (!currentUser) return;

      if (Capacitor.isNativePlatform()) {
        // Re-authenticate via Play Games native
        const result = await FirebaseAuthentication.signInWithPlayGames();
        const serverAuthCode = result.credential?.serverAuthCode || (result as any).serverAuthCode;
        if (serverAuthCode) {
          console.log("[reauthenticateUser] Syncing serverAuthCode for re-authentication...");
          const functionsInstance = getFunctions(undefined, 'us-central1');
          const exchangePlayGamesAuthCode = httpsCallable<{ serverAuthCode: string }, { success: boolean, customToken: string }>(
            functionsInstance,
            'exchangePlayGamesAuthCode'
          );
          const cloudRes = await exchangePlayGamesAuthCode({ serverAuthCode });
          if (cloudRes.data && cloudRes.data.customToken) {
            await signInWithCustomToken(auth, cloudRes.data.customToken);
          }
        }
        return;
      }

      // Web-only fallback (not used in production Android build)
      await reauthenticateWithRedirect(currentUser, new GoogleAuthProvider());
    };
    
    try {
      localStorage.setItem('block_auto_signin', 'true');
      if (unsubscribeDocRef.current) {
        unsubscribeDocRef.current();
        unsubscribeDocRef.current = null;
      }
      
      // Delete FireStore doc first
      await deleteDoc(userDocRef);
      try {
        await deleteUser(auth.currentUser);
      } catch (authErr: any) {
        if (authErr.code === 'auth/requires-recent-login') {
          console.log("Token stale for Google user. Triggering re-authentication redirect...");
          
          // Recreate firestore doc temporarily so redirect reload doesn't read user as new/tutorial bound
          if (backupUserData) {
            await setDoc(userDocRef, backupUserData);
          }
          
          await reauthenticateUser();
          
          // Unsubscribe from any new snapshot listener created by reauthenticateUser before deleting
          if (unsubscribeDocRef.current) {
            unsubscribeDocRef.current();
            unsubscribeDocRef.current = null;
          }
          
          // If we reach here (only on native, since web redirects and reloads), perform final delete
          await deleteDoc(userDocRef);
          await deleteUser(auth.currentUser);
        } else {
          throw authErr;
        }
      }
      
      localStorage.removeItem('pending_account_deletion');
      localStorage.removeItem('numq_user_has_completed_tutorial');
      sessionStorage.removeItem('nr_current_screen');
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith('shown_milestone_')) localStorage.removeItem(key);
      });
      if (Capacitor.isNativePlatform()) {
        try {
          await FirebaseAuthentication.signOut();
        } catch (signOutErr) {
          console.error("Native sign out failed during deleteAccount:", signOutErr);
        }
      }
      setUser(null);
      setLoading(false);
      setIsDeletingAccount(false);
      isDeletingRef.current = false;
    } catch (err) {
      setIsDeletingAccount(false);
      isDeletingRef.current = false;
      if (backupUserData) {
        try {
          await setDoc(userDocRef, backupUserData);
          setUser(backupUserData);
          // Re-subscribe snapshot listener on deletion failure
          unsubscribeDocRef.current = onSnapshot(userDocRef, {
            next: (docSnap) => {
              if (docSnap.exists()) {
                setUser(docSnap.data() as UserData);
              }
            }
          });
        } catch (restoreErr) {
          console.error("Failed to restore user document after deleteUser failed:", restoreErr);
        }
      }
      throw err;
    }
  };

  const logoutUser = async () => {
    localStorage.setItem('block_auto_signin', 'true');
    localStorage.removeItem('numq_user_has_completed_tutorial');
    sessionStorage.removeItem('nr_current_screen');
    if (Capacitor.isNativePlatform()) {
      try {
        await FirebaseAuthentication.signOut();
      } catch (err) {
        console.error("Native sign out failed:", err);
      }
    }
    await logout();
  };

  return {
    user, loading, isDeletingAccount, consumeHeart, deleteAccount, consumeStarTicket, addExp, restoreOneHeart, sendHeartTicket, claimGift, secondsToNextHeart,
    updateNickname, addCoins, addGems, buyTicket, buyStarTicket, redeemTicket, openAnimaTickets, refillHearts, grantMilestoneOfferRewards, expireMilestoneOffer, addSpecificBadge, addBadge, addTickets, handleGameEnd, claimDailyGift,
    addRewards, addRewardBox, buyRewardBox, openRewardBox, setPremiumStatus,
    signInWithGoogle, logout: logoutUser,
    unboxPrism, equipPrism, evolvePrismsAction, rerollPrismAction, commitReroll,
    augmentPrismAction, commitAugment, togglePrismLock, grantTutorialPrisms,
    buyAvatar: async (avatarId: string) => {
      if (!user) return false;
      const avatar = AVATARS.find(a => a.id === avatarId);
      if (!avatar || !avatar.price || user.coins < avatar.price) return false;
      if (user.purchasedAvatars?.includes(avatarId)) return false;

      try {
        await updateDoc(doc(db, 'users', user.uid), {
          coins: increment(-avatar.price),
          'counters.total_gc_spent': increment(avatar.price),
          purchasedAvatars: [...(user.purchasedAvatars || []), avatarId]
        });
        return true;
      } catch (err) {
        console.error("Error buying avatar:", err);
        return false;
      }
    },
    equipAvatar: async (avatarId: string) => {
      if (!user) return;
      // Allow equipping purchased avatars or default ones (g1-g8)
      if (!user.purchasedAvatars?.includes(avatarId) && !avatarId.startsWith('g')) return;
      await updateDoc(doc(db, 'users', user.uid), { 
        avatarId
      });
    },
    updateTutorialStep,
    completeTutorial,
    incrementAdCounter: async (type: 'heart' | 'box' | 'star_practice') => {
      if (!user) return;
      const userDocRef = doc(db, 'users', user.uid);
      const now = new Date();
      const todayStr = now.toISOString().split('T')[0];
      
      const counters = user.adCounters || {
        heartAdsCount: 0,
        lastHeartAdTime: new Date(0).toISOString(),
        lastBoxAdTime: new Date(0).toISOString(),
        starPracticeAdsCount: 0,
        lastAdResetDate: todayStr
      };

      // Daily Reset check
      const isNewDay = counters.lastAdResetDate !== todayStr;
      const updatedCounters = { ...counters };
      
      if (isNewDay) {
        updatedCounters.heartAdsCount = 0;
        updatedCounters.starPracticeAdsCount = 0;
        updatedCounters.lastAdResetDate = todayStr;
      }

      if (type === 'heart') {
        updatedCounters.heartAdsCount += 1;
        updatedCounters.lastHeartAdTime = now.toISOString();
      } else if (type === 'box') {
        updatedCounters.lastBoxAdTime = now.toISOString();
      } else if (type === 'star_practice') {
        updatedCounters.starPracticeAdsCount += 1;
      }

      await updateDoc(userDocRef, {
        adCounters: updatedCounters
      });
    }
  };
}
